// Master Apps · Primeiro acesso — vincula a matrícula TOTVS ao usuário logado.
// Solicitado uma única vez, após a aprovação e antes de liberar o catálogo.
// Página cheia (sem sidebar): fica sob RequireAuth, fora do AccessGate.
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthShell, useAuthSignOut } from '@/components/auth-shell';
import { IconCheck, IconLogout } from '@/components/icons';
import { ScreenLoading } from '@/components/loading';
import { useMeFresh, useOnboarding } from '@/hooks/use-applications';
import { byViewport, useViewport, type Viewport } from '@/hooks/use-viewport';
import {
  fontMono,
  fontSans,
  makePalette,
  numeroTabular,
  type Palette,
  rotuloCaixaAlta,
} from '@/lib/theme';
import { useScreenProps } from '@/store/theme';

type StepState = 'done' | 'active' | 'todo';

const STEPS: { label: string; state: StepState }[] = [
  { label: 'Entrar', state: 'done' },
  { label: 'Matrícula', state: 'active' },
  { label: 'Acesso', state: 'todo' },
];

/**
 * Aparência de um passo por ESTADO, em mapa de lookup em vez de cadeia de
 * ternários: concluído (preenchido no acento), ativo (anel de acento) e futuro
 * (neutro). `label` é a cor do rótulo abaixo do marcador.
 */
function stepStyle(
  state: StepState,
  p: Palette,
  accent: string,
): { background: string; color: string; border: string; label: string } {
  const byState: Record<
    StepState,
    { background: string; color: string; border: string; label: string }
  > = {
    done: { background: accent, color: '#fff', border: 'none', label: p.textMuted },
    active: {
      background: `${accent}14`,
      color: accent,
      border: `2px solid ${accent}`,
      label: p.text,
    },
    todo: {
      background: 'transparent',
      color: p.muted,
      border: `1px solid ${p.borderStrong}`,
      label: p.muted,
    },
  };
  return byState[state];
}

// Stepper horizontal de 3 passos: concluído (check, acento), ativo (anel de
// acento) e futuro (neutro). Conector herda o acento do passo anterior.
// Segue horizontal no celular: 3 marcadores de 28px caber num cartão de 280px é
// tranquilo — o que aperta é a coluna de 78px de cada rótulo, que encolhe.
function Stepper({ p, accent, vp }: Readonly<{ p: Palette; accent: string; vp: Viewport }>) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: byViewport(vp, { mobile: 22, desktop: 30 }),
      }}
    >
      {STEPS.map((step, i) => {
        const done = step.state === 'done';
        const s = stepStyle(step.state, p, accent);
        return (
          <Fragment key={step.label}>
            {i > 0 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  marginTop: 13,
                  background: STEPS[i - 1]?.state === 'done' ? accent : p.border,
                }}
              />
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 7,
                width: byViewport(vp, { mobile: 62, desktop: 78 }),
              }}
            >
              <div
                style={{
                  ...numeroTabular,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  fontSize: 12,
                  fontWeight: 700,
                  background: s.background,
                  color: s.color,
                  border: s.border,
                }}
              >
                {done ? <IconCheck size={13} stroke={2.4} /> : i + 1}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: s.label,
                }}
              >
                {step.label}
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

interface Feedback {
  message: string;
  attemptsLeft?: number;
}

export function PrimeiroAcessoPage() {
  const sp = useScreenProps();
  const p = makePalette(sp.dark);
  const vp = useViewport();
  const navigate = useNavigate();
  const handleSignOut = useAuthSignOut('primeiro-acesso');

  const meQuery = useMeFresh();
  const me = meQuery.data;
  const onboarding = useOnboarding();

  const [matricula, setMatricula] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [lockSeconds, setLockSeconds] = useState(0);
  const [focused, setFocused] = useState(false);
  const [hover, setHover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Foco no campo assim que a página monta (sem o atributo autoFocus).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Countdown do bloqueio anti força-bruta: 1 tick por segundo até zerar.
  useEffect(() => {
    if (lockSeconds <= 0) return;
    const t = setTimeout(() => setLockSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [lockSeconds]);

  // Auto-redirect: quem não precisa (ou não pode) onboardar não fica aqui.
  // Só decide com dado fresco (isFetching falso) para não reagir a cache velho.
  useEffect(() => {
    if (!me || meQuery.isFetching || onboarding.isPending) return;
    if (me.status === 'pending' || me.status === 'rejected') {
      navigate('/aguardando', { replace: true });
    } else if (!me.needsOnboarding) {
      navigate('/catalog', { replace: true });
    }
  }, [me, meQuery.isFetching, onboarding.isPending, navigate]);

  const locked = lockSeconds > 0;
  const disabled = matricula.length === 0 || onboarding.isPending || locked;

  const submit = () => {
    if (disabled) return;
    setFeedback(null);
    onboarding.mutate(matricula, {
      onSuccess: (data) => {
        navigate(data.status === 'pending' ? '/aguardando' : '/catalog', { replace: true });
      },
      onError: (err) => {
        if (err.code === 'ONBOARDING_LOCKED') {
          setLockSeconds(err.details?.retryAfterSec ?? 30);
          setFeedback({ message: 'Muitas tentativas seguidas. Aguarde para tentar novamente.' });
          return;
        }
        if (err.code === 'MATRICULA_NAO_ENCONTRADA') {
          setFeedback({
            message: 'Matrícula não encontrada na base do RH.',
            attemptsLeft: err.details?.attemptsLeft,
          });
          return;
        }
        if (err.code === 'MATRICULA_EM_USO' || err.code === 'TOTVS_ERROR') {
          setFeedback({ message: err.message });
          return;
        }
        setFeedback({
          message: err.message || 'Não foi possível verificar a matrícula. Tente novamente.',
        });
      },
    });
  };

  // Sem dado e ainda carregando: spinner padrão (o /api/me tem bypass no gate
  // da API, então funciona mesmo sem matrícula). Se der erro, a página segue
  // renderizando o formulário; o envio reporta o próprio erro.
  if (!me && !meQuery.isError) return <ScreenLoading dark={sp.dark} />;

  // Rótulo e fundo do botão de envio: o estado em curso vence o bloqueio, que
  // por sua vez vence o repouso.
  let buttonLabel = 'Confirmar matrícula';
  if (onboarding.isPending) buttonLabel = 'Verificando…';
  else if (locked) buttonLabel = `Aguarde ${lockSeconds}s`;

  let buttonBackground = sp.accent;
  if (disabled) buttonBackground = p.surface3;
  else if (hover) buttonBackground = sp.accentDark;

  // rodapé discreto: conta conectada + sair
  const accountFooter = (
    <div
      style={{
        marginTop: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        fontSize: 12,
        color: p.muted,
      }}
    >
      <button
        type="button"
        onClick={handleSignOut}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          // Sair mede 42x18 como texto puro: no celular ganha área de toque.
          padding: byViewport(vp, { mobile: '0 10px', desktop: '0' }),
          minHeight: byViewport(vp, { mobile: '44px', desktop: 'auto' }),
          color: p.muted,
          fontSize: 12,
          fontWeight: 600,
          fontFamily: fontSans,
          cursor: 'pointer',
        }}
      >
        <IconLogout size={13} />
        Sair
      </button>
    </div>
  );

  return (
    <AuthShell maxWidth={460} cardPadding="32px 34px 30px" centered={false} below={accountFooter}>
      <Stepper p={p} accent={sp.accent} vp={vp} />

      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: sp.accent,
          marginBottom: 10,
        }}
      >
        Primeiro acesso
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: '-0.022em',
          lineHeight: 1.2,
          marginBottom: 8,
        }}
      >
        Complete seu cadastro
      </div>
      <div style={{ fontSize: 13.5, color: p.muted, lineHeight: 1.6, marginBottom: 16 }}>
        Informe sua matrícula para vincular seu acesso ao cadastro do RH. Isso é solicitado uma
        única vez.
      </div>

      {/* Conta conectada — foto + nome + e-mail ANTES do campo: a pessoa vê a
          qual conta a matrícula será vinculada (antes o e-mail vivia só num
          rodapé discreto e a dúvida "estou na conta certa?" ficava sem
          resposta na tela). A foto vem do SSO via /api/me; sem foto, caem as
          iniciais no acento da marca. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          background: p.surface2,
          border: `1px solid ${p.border}`,
          borderRadius: 10,
          marginBottom: 22,
        }}
      >
        {me?.imageUrl ? (
          <img
            src={me.imageUrl}
            alt=""
            width={40}
            height={40}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              flexShrink: 0,
              background: sp.accent,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {(me?.name ?? me?.email ?? '?')
              .trim()
              .split(/\s+/)
              .map((parte) => parte[0] ?? '')
              .slice(0, 2)
              .join('')
              .toUpperCase() || '?'}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: p.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {me?.name ?? 'Conta conectada'}
          </div>
          {me?.email && (
            <div
              style={{
                fontSize: 11.5,
                color: p.muted,
                fontFamily: fontMono,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {me.email}
            </div>
          )}
        </div>
      </div>

      <label
        htmlFor="matricula"
        style={{
          ...rotuloCaixaAlta,
          display: 'block',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: p.textMuted,
          marginBottom: 8,
        }}
      >
        Matrícula
      </label>
      <input
        id="matricula"
        ref={inputRef}
        type="text"
        inputMode="numeric"
        maxLength={20}
        placeholder="Somente números"
        value={matricula}
        onChange={(e) => setMatricula(e.target.value.replace(/\D/g, '').slice(0, 20))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '13px 14px',
          borderRadius: 10,
          border: `1px solid ${focused ? sp.accent : p.borderStrong}`,
          boxShadow: focused ? `0 0 0 3px ${p.ring}` : 'none',
          background: p.inputBg,
          color: p.text,
          fontFamily: fontMono,
          fontSize: 16,
          letterSpacing: '0.08em',
          outline: 'none',
          transition: 'border-color 150ms, box-shadow 150ms',
        }}
      />

      {feedback && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 10,
            border: `1px solid ${sp.accent}55`,
            background: `${sp.accent}10`,
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, color: p.text }}>{feedback.message}</div>
          {typeof feedback.attemptsLeft === 'number' && (
            <div
              style={{
                ...numeroTabular,
                marginTop: 4,
                color: p.textMuted,
                fontSize: 11,
              }}
            >
              {feedback.attemptsLeft}{' '}
              {feedback.attemptsLeft === 1 ? 'tentativa restante' : 'tentativas restantes'} antes do
              bloqueio.
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={disabled}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: '100%',
          marginTop: 18,
          padding: '13px 16px',
          // 13+17+13 = 43px: 1px abaixo do alvo de toque de 44px no celular.
          minHeight: byViewport(vp, { mobile: '44px', desktop: 'auto' }),
          borderRadius: 10,
          border: 'none',
          background: buttonBackground,
          color: disabled ? p.muted : '#fff',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: fontSans,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background 150ms',
        }}
      >
        {buttonLabel}
      </button>
    </AuthShell>
  );
}
